import { useMemo, useCallback } from 'react';
import { useTranslations } from 'next-intl';
import { replaceTokensWithComponents } from '@cat-ecom/common/utils/stringUtils';
import { ExpiredOrderDetails } from '@cat-ecom/pcc-components';
import {
  NOTIFICATION_HEADER_STATUS,
  DYNAMIC_NOTIFICATION_TRANSLATION,
  NOTIFICATION_ORDER_LINK_TEXT
} from '../../utils/constants';
import {
  getFormattedDate,
  getRedirectionLink,
  removeFullStop
} from 'components/notifications/utils/utils';
import useMyAccountLinks from 'components/notifications/hooks/use-my-account-links';
import { useEsiteCtx } from 'services/esite/context';
import { Notification } from 'services/notifications/types';
import styles from '../../notifications.module.scss';
import stylesDetails from '../../notification-details/notification-details.module.scss';
import cx from 'classnames';
import { normalizeCommerceLink } from 'utils/links';
import NotificationCardHeaderActions from '../notification-card-header-actions/notification-card-header-actions';
import { dismissNotifications } from 'services/notifications/actions';
import { EXPS } from 'components/notifications/constants';

const NotificationCardHeader = ({
  notification,
  hoverClass = '',
  handleRefresh
}: {
  notification: Notification;
  hoverClass?: string;
  handleRefresh?: () => void;
}) => {
  const { catalogId, langId, storeId } = useEsiteCtx();
  const { dynamicLinks } = useMyAccountLinks(catalogId, storeId, langId);
  const {
    orderStatus,
    referenceId,
    orderNumber,
    orderQuoteDetailLink,
    reviewQuoteLink,
    quoteExpireDays,
    isDismissed,
    notificationId
  } = notification;
  const t = useTranslations();

  const title = t(NOTIFICATION_ORDER_LINK_TEXT[orderStatus]);

  const orderDetailLink = normalizeCommerceLink(
    getRedirectionLink(
      dynamicLinks,
      referenceId,
      orderNumber,
      orderQuoteDetailLink
    )
  );

  const handleDismiss = useCallback(async () => {
    try {
      await dismissNotifications([
        { notificationId, isDismissed: true, isRead: true }
      ]);
      handleRefresh();
    } catch (err) {
      throw new Error(err);
    }
  }, [notificationId, handleRefresh]);

  const reviewLink =
    reviewQuoteLink && !!referenceId
      ? dynamicLinks.PENDING_QUOTE_DETAIL.getUrl({
          orderId: referenceId.toString()
        })
      : null;

  const isTodayDate = useMemo(() => {
    const today = new Date();
    return getFormattedDate(today) === getFormattedDate(quoteExpireDays);
  }, [quoteExpireDays]);

  const getOrderTitle = (quote = false) => {
    const quoteNumber = orderNumber || referenceId;
    const orderNo = orderStatus !== EXPS && quoteNumber;
    return replaceTokensWithComponents(
      removeFullStop(
        t(DYNAMIC_NOTIFICATION_TRANSLATION[orderStatus], {
          '0': '{0}',
          '1': '{1}'
        })
      ),
      quote ? (
        <span
          className={styles['notification-card__text-header']}
        >{`${title} ${quote ? quoteNumber : orderNo}`}</span>
      ) : (
        <a
          href={reviewLink || orderDetailLink}
          data-testid={'link-header'}
          className={styles['notification-card__text-header']}
          tabIndex={0}
        >
          {`${title} ${quote ? quoteNumber : orderNo}`}
        </a>
      ),
      <span
        className={cx(
          styles['notification-card__text-status'],
          {
            [styles['notification-card__text-status-dismissed']]: isDismissed
          },
          hoverClass
        )}
      >
        {t(NOTIFICATION_HEADER_STATUS[orderStatus])}
      </span>
    );
  };

  return (
    <div
      className={cx(styles['notification-card__header'], 'pb-1')}
      data-testid="header-notification"
    >
      {notification.quoteExpireDays &&
      notification.orderStatus !== EXPS &&
      !isTodayDate ? (
        <ExpiredOrderDetails
          {...{
            getOrderTitle
          }}
        />
      ) : (
        <span
          data-testid="header-notification-title"
          className={styles['notification-card__text']}
        >
          {getOrderTitle()}
        </span>
      )}
      <NotificationCardHeaderActions
        isDismissed={isDismissed}
        dynamicClassName={stylesDetails['dismiss-notification-icon']}
        handleDismiss={handleDismiss}
      />
    </div>
  );
};

export default NotificationCardHeader;
